import { useMemo } from 'react';

export type AppContext = 'marketing' | 'app';

interface SubdomainInfo {
  /** Current subdomain (e.g., 'app', 'www', or null for root) */
  subdomain: string | null;
  /** Whether this is the app platform (app.domain.com or /admin, /seller, /buyer routes) */
  isAppContext: boolean;
  /** Whether this is the marketing site (www or root domain) */
  isMarketingContext: boolean;
  /** The determined context based on subdomain and route */
  context: AppContext;
  /** Full hostname */
  hostname: string;
}
// Production domain configuration
const PRODUCTION_DOMAIN = 'provenance365.com';

/**
 * Hook to detect which subdomain/context the user is on.
 * Used to show different navigation based on app.domain.com vs www.domain.com
 * 
 * Production:
 * - app.provenance365.com → app context (AppNavbar)
 * - www.provenance365.com → marketing context (MarketingNavbar)
 * - provenance365.com → marketing context (MarketingNavbar)
 * 
 * Development (localhost/lovable.app):
 * - /admin/*, /seller/*, /buyer/*, /dashboard → app context
 * - /, /about, /services, /contact, /demo → marketing context
 */
export function useSubdomain(): SubdomainInfo {
  return useMemo(() => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    
    let subdomain: string | null = null;

    // Check if we're on the production domain (provenance365.com)
    if (hostname === PRODUCTION_DOMAIN || hostname.endsWith(`.${PRODUCTION_DOMAIN}`)) {
      if (hostname !== PRODUCTION_DOMAIN) {
        // Extract subdomain: app.provenance365.com → 'app'
        subdomain = hostname.replace(`.${PRODUCTION_DOMAIN}`, '');
      }
    } 
    // Handle other production/staging domains
    else if (hostname.includes('.') && !hostname.includes('localhost') && !hostname.includes('lovable.app')) {
      const parts = hostname.split('.');
      
      // Extract subdomain for generic domains
      // e.g., app.domain.com has 3 parts
      if (parts.length >= 3) {
        subdomain = parts[0];
      }
    }
    
    // Special handling for Lovable preview URLs - use route-based detection
    if (hostname.includes('lovable.app')) {
      subdomain = null;
    }
    
    // Determine context based on subdomain first, then routes
    let isAppContext = false;
    let isMarketingContext = false;
    
    // Subdomain-based detection (takes priority)
    if (subdomain === 'app') {
      isAppContext = true;
    } else if (subdomain === 'www' || subdomain === null) {
      // Check route-based detection for development/preview
      const appRoutes = ['/admin', '/seller', '/buyer', '/dashboard'];
      const marketingRoutes = ['/', '/about', '/services', '/contact', '/demo', '/sample-report'];
      
      // Check if current path starts with any app route
      isAppContext = appRoutes.some(route => 
        pathname === route || pathname.startsWith(route + '/')
      );
      
      // If not app context, check if it's a marketing route
      if (!isAppContext) {
        isMarketingContext = marketingRoutes.some(route => 
          pathname === route || (route !== '/' && pathname.startsWith(route + '/'))
        ) || pathname === '/';
      }
    }
    
    // If neither detected, default based on path
    if (!isAppContext && !isMarketingContext) {
      // Default: if path looks like app, treat as app
      const appRoutes = ['/admin', '/seller', '/buyer', '/dashboard', '/auth'];
      isAppContext = appRoutes.some(route => pathname.startsWith(route));
      isMarketingContext = !isAppContext;
    }
    
    const context: AppContext = isAppContext ? 'app' : 'marketing';
    
    return {
      subdomain,
      isAppContext,
      isMarketingContext,
      context,
      hostname,
    };
  }, []);
}

/**
 * Get the base URL for a specific context.
 * Useful for creating links that work across subdomains.
 */
export function getContextUrl(context: AppContext): string {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // In development, just use different routes
  if (hostname === 'localhost' || hostname.includes('lovable.app')) {
    return context === 'app' ? '/dashboard' : '/';
  }
  
  // Check if we're on the production domain
  if (hostname === PRODUCTION_DOMAIN || hostname.endsWith(`.${PRODUCTION_DOMAIN}`)) {
    const subdomain = context === 'app' ? 'app' : 'www';
    return `${protocol}//${subdomain}.${PRODUCTION_DOMAIN}`;
  }
  
  // For other production domains, use subdomain pattern
  const parts = hostname.split('.');
  const baseDomain = parts.slice(-2).join('.'); // Get last 2 parts (domain.com)
  
  const subdomain = context === 'app' ? 'app' : 'www';
  const portSuffix = port ? `:${port}` : '';
  
  return `${protocol}//${subdomain}.${baseDomain}${portSuffix}`;
}
